import {
    Autocomplete, Box,
    Button,
    Card,
    CardContent, Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    Grid,
    Link,
    List,
    ListItem,
    ListItemButton,
    ListItemText, MenuItem, Pagination, Select, Stack,
    TextField
} from "@mui/material"
import {LoadingButton} from "@mui/lab";
import React, { Fragment, useContext, useEffect, useMemo, useState } from "react"
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {SnackBarContext} from "../../snackBarProvider";

import { parse, stringify } from 'lossless-json'
import {useSelector} from "react-redux";

import PrefabsRTItem from "./rt/PrefabsRTItem";
import PrefabsBAItem from "./ba/PrefabsBAItem";
import PrefabsSCItem from "./sc/PrefabsSCItem";
import PrefabsCMItem from "./cm/PrefabsCMItem";
import PrefabsTOHItem from "./toh/PrefabsTOHItem";

import {format} from "date-fns";
import {useGetPrefabsMetaQuery} from "../../core/api/toh-prefab-api";
import {ContentCopy} from "@mui/icons-material";
import SaveIcon from '@mui/icons-material/Save';
import DialogTitle from "@mui/material/DialogTitle";
import {alertMsgProcessor} from "../../core/utils/constants";
import {isEmpty} from "../../core/utils/StringUtils";
import { useBulkDeletePrefabsMutation } from "../../core/api/prefab-api";
import PrefabsDSItem from "./ds/PrefabsDSItem";
import PrefabsMCItem from "./mc/PrefabsMCItem";

const PrefabList = ({
    prefabs,
    selectedItem,
    selectItem,
    deletePrefabHandler,
    pendingDeletions,
    markForDeletion,
    unmarkForDeletion
}) => {
    const [page, setPage] = useState(1)

    const pageCount = Math.ceil(prefabs.length / 10)

    const startIndex = (page - 1) * 10
    const endIndex = startIndex + 10

    const paginatedPrefabs = prefabs.slice(startIndex, endIndex)

    useEffect(() => {
        setPage(1)
    }, [prefabs])

    const handlePageChange = (event, value) => {
        setPage(value)
    }

    const handleToggleCheckbox = (e, prefabId) => {
        if (e.target.checked) {
            markForDeletion(prefabId)
        } else {
            unmarkForDeletion(prefabId)
        }
    }

    return (
        <Stack direction="column" spacing={2}>
            <List dense disablePadding>
                {paginatedPrefabs.map((prefab, index) => (
                    <Fragment key={prefab.id}>
                        <PrefabListItem
                            item={prefab}
                            selectItem={selectItem}
                            selectedItem={selectedItem}
                            onDelete={deletePrefabHandler}
                            pendingDeletions={pendingDeletions}
                            onToggleCheckbox={handleToggleCheckbox}
                        />
                        {index < paginatedPrefabs.length - 1 && <Divider />}
                    </Fragment>
                ))}
            </List>
            {pageCount > 1 && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center"
                    }}>
                    <Pagination
                        page={page}
                        count={pageCount}
                        onChange={handlePageChange}
                    />
                </Box>
            )}
        </Stack>
    );
}

const PrefabListItem = ({ item, selectItem, selectedItem, onDelete, pendingDeletions, onToggleCheckbox }) => {
    const stringToRGB = (str) => {
        // Хэшируем строку
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }

        // Преобразуем хэш в RGB и добавляем смещение для светлой палитры
        let r = ((hash >> 16) & 0xFF) + 128; // Минимальное значение 128
        let g = ((hash >> 8) & 0xFF) + 128;  // Минимальное значение 128
        let b = (hash & 0xFF) + 128;         // Минимальное значение 128

        // Убедимся, что значения не превышают 255
        r = r > 255 ? 255 : r;
        g = g > 255 ? 255 : g;
        b = b > 255 ? 255 : b;

        // Возвращаем RGB строку
        return `rgb(${r}, ${g}, ${b})`;
    }

    return (
        <ListItem disablePadding>
            <Checkbox
                sx={{ p: 0, m: 0 }}
                disableRipple
                checked={pendingDeletions.has(item.id)}
                onChange={e => onToggleCheckbox(e, item.id)}
            />
            <ListItemButton
                onClick={() => selectItem(item)}
                selected={item.id === selectedItem.id}
            >
                <ListItemText
                    primary={item.name}
                    secondary={ <>
                        <div>{format(Date.parse(item.date), "yyyy-MM-dd HH:mm:ss")}</div>
                        {!isEmpty(item.version)&&<Chip
                            label={`${item.version}`}
                            size="small"
                            sx={{ mr: 0.5 }}
                        />}
                        {!isEmpty(item.environment)&&<Chip
                            label={`${item.environment}`}
                            size="small"
                            sx={{ mr: 0.5 }}
                        />}
                        {item.tags && item.tags.map((tag, index) => (
                            <Chip
                                key={index}
                                label={tag}
                                size="small"
                                style={{backgroundColor:stringToRGB(tag)}}
                                sx={{ mr: 0.5 }}
                            />
                        ))}
                    </>}
                />
            </ListItemButton>
        </ListItem>
    );
};

export default function Prefabs({
    game,
    prefabsListReady,
    prefabsList,
    updateUserState,
    updateUserStateResponse,
    updatePrefab,
    updatePrefabResponse,
    deletePrefab,
    deletePrefabResponse,
    refetch,
    setSelectedPrefab,
    children
}) {
    const gameAlias = {cm: 'clockmaker'}

    const {
        data: metas,
        isLoading,
        isSuccess,
        isError,
        error,
        isFetching
    } = useGetPrefabsMetaQuery(gameAlias.hasOwnProperty(game)?gameAlias[game]:game);

    const [bulkDeletePrefabs, bulkDeletePrefabsResponse] = useBulkDeletePrefabsMutation()
    const [pendingDeletions, setPendingDeletions] = useState(new Set())
    const [recentlyDeleted, setRecentlyDeleted] = useState(new Set())

    const stringToRGB = (str) => {
        // Хэшируем строку
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }

        // Преобразуем хэш в RGB и добавляем смещение для светлой палитры
        let r = ((hash >> 16) & 0xFF) + 128; // Минимальное значение 128
        let g = ((hash >> 8) & 0xFF) + 128;  // Минимальное значение 128
        let b = (hash & 0xFF) + 128;         // Минимальное значение 128

        // Убедимся, что значения не превышают 255
        r = r > 255 ? 255 : r;
        g = g > 255 ? 255 : g;
        b = b > 255 ? 255 : b;

        // Возвращаем RGB строку
        return `rgb(${r}, ${g}, ${b})`;
    }


    const CommonData = useSelector((state) => state.data.CommonData)
    let deviceId = CommonData.deviceId 
    let vendorId = (CommonData.stateId === '' || CommonData.stateId === undefined || CommonData.stateId === null) ? deviceId : CommonData.stateId //CommonData.stateId

    const [selectedItem, setSelectedItem] = useState({});
    const [itemFilter, setItemFilter] = useState({ name: "", environment: "", version: "", tags: "" });

    const [state, setState] = useState({
        isConfirmOpen: false,
        isDeleteOpen: false,
        isPrefabEditOpened: false
    })
    const useSnackBars = () => useContext(SnackBarContext);
    const {addAlert} = useSnackBars();

    const filteredPrefabs = useMemo(() => {
        const prefabs = prefabsList ?? []
        if (prefabs.length === 0) {
            return prefabs
        }

        const name = itemFilter?.name?.trim()?.toLowerCase()
        const environment = itemFilter?.environment?.trim()?.toLowerCase()
        const version = itemFilter?.version?.trim()?.toLowerCase()
        const tags = itemFilter?.tags?.trim()?.toLowerCase()

        return prefabs.filter(prefab => {
            if (recentlyDeleted.has(prefab.id)) {
                return false
            }
            if (name && !prefab?.name?.trim()?.toLowerCase()?.includes(name)) {
                return false
            }
            if (environment && prefab?.environment?.trim()?.toLowerCase() !== environment) {
                return false
            }
            if (version && prefab?.version?.trim()?.toLowerCase() !== version) {
                return false
            }
            if (tags && !prefab?.tags?.map(tag => tag?.trim()?.toLowerCase()).includes(tags)) {
                return false
            }
            return true
        })
    }, [prefabsList, itemFilter, recentlyDeleted])

    useEffect(() => {
        setPendingDeletions(new Set())
    }, [itemFilter])

    function selectItem(item) {
        setSelectedItem(item)
        updatePrefabForm(item)
        if(setSelectedPrefab!=undefined) setSelectedPrefab(item)
    }

    function showConfirmDialog(item) {
        setSelectedItem(item)
        setState({
            ...state,
            isConfirmOpen: true
        })
    }

    function showDeleteDialog(item) {
        setSelectedItem(item)
        setState({
            ...state,
            isDeleteOpen: true
        })
    }

    async function handleConfirmApplyState(event) {
        setState({
            ...state,
            isConfirmOpen: false
        })

        let version = window.localStorage.getItem('isProd') == 'true' ? 'v2' : 'v2';

        if(game != 'cm') version = 'v2'

        let dataString = ''
        try {
            dataString = stringify(parse(selectedItem.data))
        } catch (e) {
            dataString = selectedItem.data
        }

        const response = await updateUserState({
            userId: deviceId,
            vendorId: (vendorId === '' || vendorId === undefined || vendorId === null) ? deviceId : vendorId,
            game: game,
            body: dataString,
            version: version
        })

        // setOpenSnackbar(response.error)

        if (response.error) {
            if (response.error.status === 403) {
                addAlert({text: 'This id is not in the whitelist', type: 'error'})
            } else {
                addAlert({text: 'Something went wrong', type: 'error'})
            }
        } else {
            addAlert({text: 'State applied', type: 'success'})
        }

    }

    async function updatePrefabHandler(selectedItem) {
        if(!selectedItem.hasOwnProperty('data') || selectedItem.data==null) return false;

        selectedItem = {...selectedItem, data: stringify(parse(selectedItem.data))}

        const response = await updatePrefab({
            id: selectedItem.id,
            body: selectedItem
        });

        if (response.error) {
            if (response.error.status === 403) {
                addAlert({text: JSON.stringify(response.error), type: 'error'})
            } else {
                addAlert({text: 'Something went wrong ' + response.error.status, type: 'error'})
            }
        } else {
            addAlert({text: 'Prefab updated', type: 'success'})
        }
    }

    async function deletePrefabHandler(selectedItem) {

        const response = await deletePrefab(selectedItem.id);
        if (response.error) {
            if (response.error.status === 403) {
                addAlert({text: JSON.stringify(response.error), type: 'error'})
            } else {
                addAlert({text: 'Something went wrong ' + response.error.status, type: 'error'})
            }
        } else {
            addAlert({text: 'Prefab deleted', type: 'success'})
        }

        setSelectedItem({})
        setState({
            ...state,
            isDeleteOpen: false
        })
    }

    const sortVersions = (versions) => {
        let _versions = JSON.parse(JSON.stringify(versions))

        return _versions.sort((a, b) => {
            try {
                const aParts = a.split('.').map(Number);
                const bParts = b.split('.').map(Number);

                for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
                    const aPart = aParts[i] || 0;
                    const bPart = bParts[i] || 0;

                    if (aPart > bPart) return -1;
                    if (aPart < bPart) return 1;
                }
            } catch (e) {
                return 0
            }

            return 0;
        }).filter(item => !isEmpty(item));
    }

    const sortedVersions = metas!=undefined&&metas.hasOwnProperty('version') ? sortVersions(metas.version) : []

    let metaTags = []
    if(metas!=undefined&&prefabsList!=undefined) {

        if(metas.tags.length>0) metaTags = metas.tags
        else {
            metaTags = prefabsList.reduce((a, b) => {

                return a.concat(b.tags || [])
            }, []);
        }
    }

    const [prefabForm, updatePrefabForm] = useState({
        name: '',
        env: '',
        version: '',
        tags: [],
        environment:''
    })


    async function createPrefabHandler() {
        const request = {
            id: selectedItem.id,
            body: {
                "data": selectedItem.data,
                "name": prefabForm.name,
                "tags": prefabForm.tags,
                "version": prefabForm.version,
                "environment": prefabForm.environment
            },
        };
        let response = false

        response = await updatePrefab(request)

        setState({...state, isPrefabEditOpened: false})



        alertMsgProcessor(response, addAlert)


    }

    const markForDeletion = (prefabId) => {
        setPendingDeletions(prevState => {
            const nextState = new Set(prevState)
            nextState.add(prefabId)
            return nextState
        })
    }

    const unmarkForDeletion = (prefabId) => {
        setPendingDeletions(prevState => {
            const nextState = new Set(prevState)
            nextState.delete(prefabId)
            return nextState
        })
    }

    const handleChangeSelectAllCheckbox = (e) => {
        if (e.target.checked) {
            setPendingDeletions(new Set(filteredPrefabs.map(prefab => prefab.id)))
        } else {
            setPendingDeletions(new Set())
        }
    }

    const handleClickDeleteSelectedButton = () => {
        if (window.confirm(`Delete ${pendingDeletions.size} prefabs?`)) {
            bulkDeletePrefabs({
                "project": gameAlias[game] ?? game,
                "prefabIds": pendingDeletions
            })
            setRecentlyDeleted(prevState => new Set([...prevState, ...pendingDeletions]))
            setPendingDeletions(new Set())
        }
    }

    return (
        <>
            <Card>
                <CardContent>
                    <Stack direction="column" spacing={2}>
                    {metas !== undefined && !isLoading && (
                        <Stack direction="row" spacing={2}>
                            <TextField
                                size="small"
                                fullWidth
                                label="Filter by name"
                                value={itemFilter.name}
                                onChange={(e) => setItemFilter({ ...itemFilter, name: e.target.value })}
                            />
                            <Select
                                size="small"
                                fullWidth
                                displayEmpty
                                value={itemFilter.environment}
                                onChange={(e) => setItemFilter({ ...itemFilter, environment: e.target.value })}
                            >
                                <MenuItem value="">All Environments</MenuItem>
                                {metas?.environment?.filter(item => !isEmpty(item)).map((env, index) => (
                                    <MenuItem key={index} value={env}>{env}</MenuItem>
                                ))}
                            </Select>
                            <Select
                                size="small"
                                fullWidth
                                displayEmpty
                                value={itemFilter.version}
                                onChange={(e) => setItemFilter({ ...itemFilter, version: e.target.value })}
                            >
                                <MenuItem value="">All Versions</MenuItem>
                                {sortedVersions.map((version, index) => (
                                    <MenuItem key={index} value={version}>{version}</MenuItem>
                                ))}
                            </Select>
                            <Select
                                size="small"
                                fullWidth
                                displayEmpty
                                value={itemFilter.tags}
                                onChange={(e) => setItemFilter({ ...itemFilter, tags: e.target.value })}
                            >
                                <MenuItem value="">All Tags</MenuItem>
                                {metaTags.map((tag, index) => (
                                    <MenuItem key={index} value={tag}>{tag}</MenuItem>
                                ))}
                            </Select>
                        </Stack>
                    )}

                    <Grid container direction="row" spacing={2}>
                        <Grid size={4}>
                            {prefabsListReady && (
                                <Stack direction="column" spacing={2}>
                                    <Stack direction="row" spacing={2} sx={{
                                        justifyContent: "space-between"
                                    }}>
                                        <Stack direction="row" spacing={1} sx={{
                                            alignItems: "center"
                                        }}>
                                            <Checkbox
                                                sx={{ p: 0, m: 0 }}
                                                disableRipple
                                                checked={pendingDeletions.size > 0}
                                                onChange={handleChangeSelectAllCheckbox}
                                            />
                                            <span>Select All</span>
                                        </Stack>
                                        <LoadingButton
                                            disabled={bulkDeletePrefabsResponse.isLoading || pendingDeletions.size === 0}
                                            variant="outlined"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={handleClickDeleteSelectedButton}
                                        >
                                            Delete Selected
                                        </LoadingButton>
                                    </Stack>
                                    <PrefabList
                                        prefabs={filteredPrefabs}
                                        selectedItem={selectedItem}
                                        selectItem={selectItem}
                                        deletePrefabHandler={deletePrefabHandler}
                                        pendingDeletions={pendingDeletions}
                                        markForDeletion={markForDeletion}
                                        unmarkForDeletion={unmarkForDeletion}
                                    />
                                </Stack>
                            )}
                        </Grid>
                        <Grid size={8}>
                            {selectedItem.hasOwnProperty("id") &&
                                <Stack direction="column" spacing={2}>
                                    <Stack direction="row" spacing={1}>
                                        <LoadingButton
                                            disabled={!selectedItem.hasOwnProperty('id')}
                                            loading={updateUserStateResponse.isLoading}
                                            color={"success"}
                                            variant={"outlined"}
                                            startIcon={<CheckCircleIcon/>}
                                            onClick={() => showConfirmDialog(selectedItem)}>
                                            Apply this
                                        </LoadingButton>
                                        <LoadingButton
                                            disabled={!selectedItem.hasOwnProperty('id')}
                                            loading={updatePrefabResponse.isLoading}
                                            color={"info"}
                                            startIcon={<SaveIcon />}
                                            variant={"outlined"}
                                            onClick={() => updatePrefabHandler(selectedItem)}>
                                            Save prefab
                                        </LoadingButton>
                                        <Button
                                            variant={"outlined"}
                                            disabled={!selectedItem.hasOwnProperty('id')}
                                            color={"info"}
                                            startIcon={<ContentCopy />}
                                            onClick={() => {
                                                navigator.clipboard.writeText(selectedItem.data)
                                                addAlert({text: 'JSON Copied', type: 'success'})
                                            }}>
                                            Copy to clipboard
                                        </Button>
                                        <LoadingButton
                                            variant={"outlined"}
                                            disabled={!selectedItem.hasOwnProperty('id')}
                                            loading={deletePrefabResponse.isLoading}
                                            color={"error"}
                                            startIcon={<DeleteIcon/>}
                                            onClick={() => showDeleteDialog(selectedItem)}>
                                            Delete
                                        </LoadingButton>
                                    </Stack>

                                    <Stack direction="row" spacing={1}>
                                        <Chip
                                            label={`Date: ${selectedItem.date}`}
                                            size="small"
                                            sx={{ mr: 0.5 }}
                                        />
                                        <Chip
                                            label={`version: ${selectedItem.version}`}
                                            size="small"
                                            sx={{ mr: 0.5 }}
                                        />
                                        <Chip
                                            label={`env: ${selectedItem.environment}`}
                                            size="small"
                                            sx={{ mr: 0.5 }}
                                        />
                                        {selectedItem?.tags && selectedItem?.tags?.map((tag, index) => (
                                            <Chip
                                                key={index}
                                                label={tag}
                                                style={{backgroundColor: stringToRGB(tag)}}
                                                size="small"
                                                sx={{ mr: 0.5 }}
                                            />
                                        ))}

                                        <Link onClick={() => {
                                            setState({...state, isPrefabEditOpened:true});
                                            return false;
                                        }}
                                              href={'#'}
                                                sx={{
                                                    pl: 1,
                                                    textDecoration: 'underline dotted',
                                                    color: 'primary.main', // You can adjust this to match the color of a typical link
                                                    backgroundColor: 'transparent',
                                                    minWidth: 0, // Removes extra padding
                                                    '&:hover': {
                                                        textDecoration: 'underline dotted',
                                                        backgroundColor: 'transparent',
                                                    },
                                                }}
                                        >edit</Link>
                                    </Stack>

                                    {game=='rt' && <PrefabsRTItem selectedItem={selectedItem} setSelectedItem={setSelectedItem} />}
                                    {game=='ba' && <PrefabsBAItem selectedItem={selectedItem} setSelectedItem={setSelectedItem} />}
                                    {game=='sc' && <PrefabsSCItem selectedItem={selectedItem} setSelectedItem={setSelectedItem} />}
                                    {game=='cm' && <PrefabsCMItem selectedItem={selectedItem} setSelectedItem={setSelectedItem} />}
                                    {game=='toh' && <PrefabsTOHItem selectedItem={selectedItem} setSelectedItem={setSelectedItem} />}
                                    {game=='ds' && <PrefabsDSItem selectedItem={selectedItem} setSelectedItem={setSelectedItem} />}
                                    {game=='mc' && <PrefabsMCItem selectedItem={selectedItem} setSelectedItem={setSelectedItem} />}
                                </Stack>
                            }
                        </Grid>
                    </Grid>
                    </Stack>
                </CardContent>
            </Card>
            <Dialog open={state.isConfirmOpen}>
                <DialogContent>
                    {deviceId == null ? <>Введите state id в панеле сверху и нажмите search</>:
                        <>Apply state <br/>
                            <strong>{selectedItem.name}</strong>
                            <br/> for state id:<br/>
                            <strong>{deviceId}</strong>
                            <br/> and vendor Id:<br/>
                            <strong>{vendorId}</strong></>
                    }
                </DialogContent>
                <DialogActions>
                    <Button
                        
                        onClick={() => {
                            setState({...state, isConfirmOpen: false})
                        }}>
                        Cancel
                    </Button>
                    <LoadingButton
                        onClick={handleConfirmApplyState}
                        variant="outlined"
                        color="warning"
                    >
                        APPLY
                    </LoadingButton>

                </DialogActions>
            </Dialog>
            <Dialog open={state.isDeleteOpen}>
                <DialogContent>
                        Delete prefab  <br/>
                        <strong>{selectedItem.name}</strong>
                </DialogContent>
                <DialogActions>
                    <Button
                        
                        onClick={() => {
                            setState({...state, isDeleteOpen: false})
                        }}>
                        Cancel
                    </Button>
                    <LoadingButton
                        onClick={() => { deletePrefabHandler(selectedItem) }}
                        variant="outlined"
                        color="warning"
                    >
                        DELETE
                    </LoadingButton>

                </DialogActions>
            </Dialog>
            <Dialog sx={{ '& .MuiDialog-paper': { width: '400px' } }} open={state.isPrefabEditOpened}>
                <DialogTitle>
                    Update prefab

                </DialogTitle>
                <DialogContent>
                    <Grid container direction="column" spacing={2}>


                        <Grid sx={{mt:1}}>
                            <TextField
                                label="Name"
                                value={prefabForm.name}
                                required={true}
                                onChange={(event) => {
                                    updatePrefabForm({
                                        ...prefabForm,
                                        name: event.target.value
                                    })
                                }}></TextField>
                        </Grid>

                        <Grid>
                            <Autocomplete
                                options={metas?.environment || []}
                                freeSolo
                                value={prefabForm.environment}
                                onChange={(event, newValue) => {
                                    updatePrefabForm({
                                        ...prefabForm,
                                        environment: newValue
                                    });
                                }}
                                onInputChange={(event, newInputValue) => {
                                    updatePrefabForm({
                                        ...prefabForm,
                                        environment: newInputValue || '' // Save input value
                                    });
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Environment" variant="outlined" fullWidth />
                                )}
                            />
                        </Grid>

                        <Grid>
                            <Autocomplete
                                options={sortedVersions || []}
                                freeSolo
                                value={prefabForm.version}
                                onChange={(event, newValue) => {
                                    updatePrefabForm({
                                        ...prefabForm,
                                        version: newValue
                                    });
                                }}
                                onInputChange={(event, newInputValue) => {
                                    updatePrefabForm({
                                        ...prefabForm,
                                        version: newInputValue || '' // Save input value
                                    });
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Version" variant="outlined" fullWidth />
                                )}
                            />
                        </Grid>

                        <Grid>
                            <Autocomplete

                                multiple
                                options={metas?.tags || []}
                                freeSolo
                                value={prefabForm.tags || []}
                                onChange={(event, newValue) => {

                                    updatePrefabForm({
                                        ...prefabForm,
                                        tags: newValue || []
                                    });
                                }}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip variant="outlined" label={option} key={index} {...getTagProps({ index })} />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField helperText="Use , (comma) or 'Enter' to split tags"
                                                                               {...params} label="Tags" variant="outlined" fullWidth />
                                )}
                                onInputChange={(event, newInputValue, reason) => {
                                    if (newInputValue.endsWith(',')) {
                                        // Split the input based on commas and trim the values
                                        const splitValues = newInputValue
                                            .split(',')
                                            .map(tag => tag.trim())
                                            .filter(tag => tag.length > 0);

                                        // Combine with existing tags, ensuring no duplicates
                                        const updatedTags = [...new Set([...prefabForm.tags, ...splitValues])];

                                        // Update the form state with new tags
                                        updatePrefabForm({
                                            ...prefabForm,
                                            tags: updatedTags
                                        });

                                        // Clear the input field by setting the input value to an empty string
                                        setTimeout(() => {
                                            event.target.value = '';
                                        }, 10);
                                    }
                                }}
                            />
                        </Grid>


                    </Grid>
                </DialogContent>
                <DialogActions>

                    <Button
                        
                        onClick={() => {
                            setState({...state, isPrefabEditOpened: false})
                        }}>
                        Cancel
                    </Button>
                    <LoadingButton
                        onClick={createPrefabHandler}
                        variant="outlined"
                        color="warning"
                        loading={updatePrefab.isLoading}
                        disabled={prefabForm.name=='' || prefabForm.env==''}
                    >
                        SAVE
                    </LoadingButton>

                </DialogActions>
            </Dialog>
        </>
    );
}
